import { Badge, Field, Input, Table, Text } from "@chakra-ui/react";
import { AlertCircle, CheckCircle } from "lucide-react";
import { BatchColumn, BatchFieldError } from "./batchPreview";

type Props = {
  columns: BatchColumn[];
  rows: Record<string, unknown>[];
  order: number[];
  errors: BatchFieldError[][];
  onEdit: (index: number, key: string, value: string) => void;
  onBlur: (index: number, key: string) => void;
};

export function BatchPreviewTable({
  columns,
  rows,
  order,
  errors,
  onEdit,
  onBlur,
}: Props) {
  return (
    <Table.ScrollArea borderWidth="1px" borderRadius="md" maxH="50vh">
      <Table.Root size="sm" stickyHeader>
        <Table.Header>
          <Table.Row bg="#F7F9FC">
            <Table.ColumnHeader minW="100px">Estado</Table.ColumnHeader>
            {columns.map((column) => (
              <Table.ColumnHeader
                key={column.key}
                minW={column.minWidth}
                textAlign={column.align}
              >
                {column.label}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {order.map((index) => {
            const row = rows[index];
            const hasError = errors[index].length > 0;

            return (
              <Table.Row key={index} bg={hasError ? "red.50" : "white"}>
                <Table.Cell verticalAlign="top">
                  <Badge
                    colorPalette={hasError ? "red" : "green"}
                    variant="subtle"
                    gap={1}
                  >
                    {hasError ? (
                      <AlertCircle size={12} />
                    ) : (
                      <CheckCircle size={12} />
                    )}
                    {hasError ? "Error" : "Listo"}
                  </Badge>
                </Table.Cell>
                {columns.map((column) => {
                  const error = errors[index].find(
                    (e) => e.field === column.key
                  );
                  const value = row[column.key];

                  return (
                    <Table.Cell
                      key={column.key}
                      verticalAlign="top"
                      textAlign={column.align}
                    >
                      {error ? (
                        <Field.Root invalid gap={1} minW="140px">
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color="red.600"
                          >
                            {error.message}
                          </Text>
                          <Input
                            size="xs"
                            bg="white"
                            inputMode={column.inputMode}
                            value={String(value ?? "")}
                            onChange={(e) =>
                              onEdit(index, column.key, e.target.value)
                            }
                            onBlur={() => onBlur(index, column.key)}
                          />
                        </Field.Root>
                      ) : (
                        <Text fontSize="sm" whiteSpace="nowrap">
                          {column.format
                            ? column.format(value)
                            : String(value ?? "")}
                        </Text>
                      )}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}
